import axios from 'axios';
import supabase from './supabaseClient';
import { resolverUrl, subirArchivo } from '../services/storageService';

// URL base de la API
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

// Función auxiliar para obtener el token de autenticación actual
const getAuthToken = async () => {
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) return data.session.access_token;

    const { data: refreshData } = await supabase.auth.refreshSession();
    return refreshData?.session?.access_token || '';
  } catch (error) {
    console.error('Error getting auth token:', error);
    return '';
  }
};

const authHeader = async () => ({ Authorization: `Bearer ${await getAuthToken()}` });

// Función para obtener todas las imágenes
export const fetchImages = async () => {
  const { data } = await axios.get(`${API_URL}/api/images`);
  return data.map((img) => ({ ...img, url: resolverUrl(img.url) }));
};

// Función para obtener imágenes por perfil
export const fetchProfileImages = async (profileId) => {
  const { data } = await axios.get(`${API_URL}/api/images/${profileId}`);
  return data.map((img) => ({ ...img, url: resolverUrl(img.url) }));
};

/**
 * Videos.
 *
 * Antes esto consultaba Supabase directo desde el navegador. El problema es que
 * las filas guardan `url` apuntando a cdn.gallerygg.site, un dominio que ya no
 * existe: leyendo la tabla en crudo, los videos nunca cargan. La API reescribe
 * esas URLs a partir de la key, asi que hay que pasar por ella.
 */
export const fetchVideos = async () => {
  const { data } = await axios.get(`${API_URL}/api/videos`);
  return data.map((v) => ({
    ...v,
    url: resolverUrl(v.url),
    thumbnail_url: resolverUrl(v.thumbnail_url),
  }));
};

export const fetchProfileVideos = async (profileId) => {
  const { data } = await axios.get(`${API_URL}/api/videos`, { params: { profile_id: profileId } });
  return data.map((v) => ({
    ...v,
    url: resolverUrl(v.url),
    thumbnail_url: resolverUrl(v.thumbnail_url),
  }));
};

/**
 * Sube una imagen: primero el binario por streaming, despues los metadatos.
 *
 * Acepta un File o un FormData (por compatibilidad con los formularios que ya
 * armaban uno). En ambos casos termina haciendo PUT del archivo crudo, porque
 * multipart obligaria al backend a bufferizarlo entero en memoria.
 */
export const uploadImage = async (entrada, profileIdExplicito) => {
  const esFormData = typeof FormData !== 'undefined' && entrada instanceof FormData;

  const file = esFormData ? entrada.get('image') : entrada;
  const profileId = profileIdExplicito || (esFormData ? entrada.get('profile_id') : undefined);

  if (!file) throw new Error('No se recibio ningun archivo para subir');

  const metadata = esFormData
    ? {
        title: entrada.get('title') || undefined,
        description: entrada.get('description') || undefined,
        tags: entrada.get('tags') || undefined,
      }
    : {};

  const subido = await subirArchivo(file, profileId);

  const { data } = await axios.post(
    `${API_URL}/api/images`,
    { ...metadata, key: subido.key, url: subido.url, profile_id: profileId },
    { headers: await authHeader() },
  );

  return data;
};

// La lista de colecciones cambia poco y se vuelve a ella todo el tiempo. Se
// guarda para que al regresar la grilla se dibuje de inmediato en vez de
// esperar a la red; la peticion igual se hace y actualiza lo que haya cambiado.
let perfilesEnMemoria = null;

export const perfilesCacheados = () => perfilesEnMemoria;

// Función para obtener perfiles
export const fetchProfiles = async () => {
  try {
    const { data } = await axios.get(`${API_URL}/api/profiles`);
    perfilesEnMemoria = data.map((p) => ({ ...p, cover_image: resolverUrl(p.cover_image) }));
    return perfilesEnMemoria;
  } catch (error) {
    console.error('Error fetching profiles:', error);
    return perfilesEnMemoria || [];
  }
};

// Función para obtener un perfil específico
export const fetchProfile = async (profileId) => {
  try {
    const { data } = await axios.get(`${API_URL}/api/profiles/${profileId}`);
    return { data: { ...data, cover_image: resolverUrl(data.cover_image) }, error: null };
  } catch (error) {
    console.error('Error fetching profile:', error);
    return { data: null, error };
  }
};

/**
 * Crea o actualiza un perfil.
 *
 * La portada ya no viaja dentro de este request: se sube antes por streaming y
 * aca solo van sus referencias. Asi este endpoint nunca maneja binarios.
 */
export const updateProfile = async (profileId, profileData, coverImage) => {
  const cuerpo = {
    name: profileData.name,
    description: profileData.description || '',
  };
  if (profileId) cuerpo.id = profileId;

  if (coverImage) {
    const portada = await subirArchivo(coverImage, profileId || 'profile-covers', {
      carpeta: 'covers',
    });
    cuerpo.cover_image = portada.url;
    cuerpo.cover_image_key = portada.key;
  }

  const { data } = await axios.post(`${API_URL}/api/profiles`, cuerpo, {
    headers: await authHeader(),
  });

  return { ...data, cover_image: resolverUrl(data.cover_image) };
};

// Función para eliminar una imagen (solo para administradores)
export const deleteImage = async (imageId) => {
  const token = await getAuthToken();
  if (!token) throw new Error('No estás autenticado');

  const { data } = await axios.delete(`${API_URL}/api/images/${imageId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return data;
};

// Función para eliminar un video
export const deleteVideo = async (videoId) => {
  const token = await getAuthToken();
  if (!token) throw new Error('No estás autenticado');

  // Sin fallback a Supabase directo: borrar solo la fila dejaria el archivo
  // huerfano en R2, ocupando espacio para siempre. La API borra las dos cosas.
  const { data } = await axios.delete(`${API_URL}/api/videos/${videoId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  return data;
};
